/**
 * External dependencies.
 */
import type { GetStaticPaths, GetStaticProps } from "next";

/**
 * Internal dependencies.
 */
import {
  fetchAPI,
  getLocale,
  getLocalizationData,
  getSlug,
  loadTranslation,
} from "utils";
import Layout from "layout/layout";
import PageDocSlug from "blocks/page-doc-slug/page-doc-slug";

const DocumentationSingle = (props: any) => {
  return (
    <Layout
      title="Documentation"
      desc="Documentation"
      light
      notice
      hreflang={false}
    >
      <PageDocSlug data={props} />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data: globalData } = await fetchAPI("/global", {
    populate: "*",
    locale,
  });
  const { data } = await fetchAPI("/categories", {
    populate: {
      documentations: {
        populate: {
          themes: "*",
        },
      },
    },
    sort: "order:asc",
    locale,
  });

  const { data: current } = await fetchAPI(`/documentations`, {
    populate: {
      SEO: {
        populate: "*",
      },
      category: "*",
      Title: "*",
      Content: "*",
      Slug: "*",
      slug: "*",
      Useful: "*",
      NotUseful: "*",
      themes: {
        populate: {
          localizations: "*",
        },
      },
      apps: {
        populate: {
          localizations: "*",
        },
      },
      order: "*",
      localizations: {
        populate: {
          category: "*",
          themes: "*",
          apps: "*",
        },
      },
    },
    sort: "order:asc",
    filters: {
      $or: [
        {
          slug_overwrite: ctx.params?.slug,
          themes: {
            $or: [
              {
                slug_overwrite: ctx.params?.theme,
              },
              {
                slug: ctx.params?.theme,
              },
            ],
          },
        },
        {
          slug: ctx.params?.slug,
          themes: {
            $or: [
              {
                slug_overwrite: ctx.params?.theme,
              },
              {
                slug: ctx.params?.theme,
              },
            ],
          },
        },
      ],
    },
    pagination: {
      pageSize: 9999,
    },
    locale,
  });

  const { data: helpData } = await fetchAPI("/help-centre-landing", {
    populate: "*",
    locale,
  });

  const getCurrent = current.pop();

  // Get icon set if documentation is related to icons
  let iconSet: any = null;
  let iconGroups: any = null;
  if (
    getCurrent &&
    getCurrent?.attributes?.Title.toLowerCase().includes("icon") &&
    !getCurrent?.attributes?.Title.toLowerCase().includes("favicon")
  ) {
    const { data: iconSetResponse } = await fetchAPI(`/theme-icons`, {
      populate: "*",
      filters: {
        themes: {
          Title: {
            $contains: getCurrent.attributes.themes.data.map(
              (item: any) => item.attributes.Title
            ),
          },
        },
      },
      pagination: {
        pageSize: 9999,
      },
    });

    iconSet = iconSetResponse;

    const { data: iconGroupResponse } = await fetchAPI(`/theme-icon-groups`, {
      populate: {
        Label: "*",
        theme: "*",
      },
      pagination: {
        pageSize: 9999,
      },
    });

    iconGroups = iconGroupResponse;
  }

  return {
    props: {
      translation,
      helpData,
      app: false,
      categories: data,
      current: getCurrent,
      iconSet,
      iconGroups,
      globalData,
      locale: ctx.locale,
      localizationData: getLocalizationData(getCurrent, "", "themes"),
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  let paths: any = [];

  const { data } = await fetchAPI("/documentations", {
    populate: {
      themes: "*",
      localizations: {
        populate: {
          themes: "*",
        },
      },
    },
    pagination: {
      pageSize: 9999,
    },
  });

  data?.map(({ attributes }: any) => {
    if (attributes.localizations.data) {
      attributes.localizations.data.map((locItem: any) => {
        if (locItem.attributes.themes.data) {
          locItem.attributes.themes.data.map((locTheme: any) => {
            paths.push({
              params: {
                theme: getSlug(locTheme.attributes),
                slug: getSlug(locItem.attributes),
              },
              locale: locItem.attributes.locale.toLowerCase(),
            });
          });
        }
      });
    }

    attributes.themes.data.map((theme: any) => {
      paths.push({
        params: {
          theme: getSlug(theme.attributes),
          slug: getSlug(attributes),
        },
        locale: attributes.locale.toLowerCase(),
      });
    });
  });

  return {
    paths,
    fallback: false,
  };
};

export default DocumentationSingle;
